import { useEffect, useState } from 'react'
import styled from 'styled-components'

import Bugsnag from '@bugsnag/js'
import { useGetOrderQuery, useUpdateOrderStatusMutation } from 'api/ordersApi'
import ErrorBanner from 'errors/components/ErrorBanner'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { t } from 'localization'
import { isFunction } from 'lodash/fp'
import Button from 'shared/components/Button'
import { SVGIcon } from 'shared/components/Icons/SVGIcons/SVGIcon'
import PhotosGrid from 'shared/components/PhotosGrid'
import SideSheet from 'shared/components/SideSheet'
import SideSheetModal from 'shared/components/SideSheetModal'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { colors, fonts } from 'shared/lib'
import { sizes } from 'shared/lib/media'
import { IBuyerAction } from 'shared/types/IBuyerAction'

const TP = 'account.components.orderIssuesPanel'

enum StatusAction {
  DiscountAccepted = 'discount_accepted',
}

type OrderIssuesPanelProps = {
  isHeightFitContent?: boolean
  isVisible: boolean
  onClose: () => void
  orderNumber: number
  paymentTypeDisplay: string
}

export const OrderIssuesPanel = ({
  isHeightFitContent = false,
  isVisible,
  onClose,
  orderNumber,
  paymentTypeDisplay,
}: OrderIssuesPanelProps) => {
  const isOrderCancellationEnabled = useFeatureFlag(FeatureFlag.WEB_FC_ORDER_CANCELLATION)
  const [isConfirming, setIsConfirming] = useState(false)
  const [errors, setErrors] = useState([])
  const [confirmationTitle, setConfirmationTitle] = useState('')
  const [confirmationMessage, setConfirmationMessage] = useState('')
  const [chosenBuyerAction, setChosenBuyerAction] = useState('')
  const [isIssueResolved, setIsIssueResolved] = useState(false)
  const [acceptedDiscountAmount, setAcceptedDiscountAmount] = useState('')
  const [acceptedDiscountPercent, setAcceptedDiscountPercent] = useState(0)

  const {
    data,
    error: getOrderError,
    isError: isGetOrderError,
    isLoading: isGetOrderLoading,
  } = useGetOrderQuery(orderNumber)
  const [
    updateOrderStatus,
    {
      data: updateOrderStatusData,
      error: updateOrderStatusError,
      isError: isUpdateOrderStatusError,
      isLoading: isUpdateOrderStatusLoading,
      isSuccess: isUpdateOrderStatusSuccess,
    },
  ] = useUpdateOrderStatusMutation()

  useEffect(() => {
    if (isGetOrderError || isUpdateOrderStatusError) {
      const error = getOrderError || updateOrderStatusError
      const messages =
        error &&
        ('status' in error
          ? error?.data?.localized_messages || error?.data?.messages
          : [error.message])

      Bugsnag.notify(
        new Error(
          `OrderIssuesPanel: updateOrderStatus to ${chosenBuyerAction}
         for ${orderNumber}: ${messages}`,
        ),
      )
      setErrors(messages)
    }
  }, [
    isGetOrderError,
    isUpdateOrderStatusError,
    getOrderError,
    updateOrderStatusError,
    orderNumber,
    chosenBuyerAction,
  ])

  useEffect(() => {
    if (isUpdateOrderStatusSuccess) {
      setIsIssueResolved(true)
    }
  }, [isUpdateOrderStatusSuccess])

  if (!isOrderCancellationEnabled || isGetOrderError || isGetOrderLoading) {
    return null
  }

  const {
    buyerActions,
    buyerDescription,
    buyerDiscountAmountFormatted,
    buyerDiscountPercentage,
    buyerIssuePictures,
    buyerTitle,
  } = updateOrderStatusData ? updateOrderStatusData : data

  const handleResolutionSelection = (buyerAction: IBuyerAction) => {
    const {
      confirmTitle = t(`${TP}.confirmTitle`, 'Confirm'),
      confirmMessage = '',
      buttonAction = '',
    } = buyerAction
    setConfirmationTitle(confirmTitle)
    setConfirmationMessage(confirmMessage)
    setChosenBuyerAction(buttonAction)
    setIsConfirming(true)
  }

  const handleConfirmationSubmit = async () => {
    const body = {
      orderNumber,
      order: {
        statusAction: chosenBuyerAction,
      },
    }
    if (chosenBuyerAction === StatusAction.DiscountAccepted) {
      setAcceptedDiscountAmount(buyerDiscountAmountFormatted)
      setAcceptedDiscountPercent(buyerDiscountPercentage)
    }
    await updateOrderStatus(body)
    setIsConfirming(false)
  }

  const handleClose = () => {
    setErrors([])
    if (isFunction(onClose)) {
      onClose()
    }
  }

  const discountPercentageDisplay = buyerDiscountPercentage || acceptedDiscountPercent
  const discountAmountDisplay = buyerDiscountAmountFormatted || acceptedDiscountAmount

  return (
    <SideSheet.Responsive
      title={t(`${TP}.sideSheetResponseTitle`, {
        defaultValue: 'Order #{orderNumber}',
        orderNumber,
      })}
      isVisible={isVisible}
      onClose={handleClose}
      isFooterBoxShadowVisible={!!buyerActions?.length}
      isHeightFitContent={isHeightFitContent}
      data-testid="orderIssuesSidesheet"
    >
      <SideSheet.ModalOverlay>
        <SideSheetModal data-testid="confirmationModal" isOpen={isConfirming}>
          <ModalHeader>{confirmationTitle}</ModalHeader>
          <ModalDescription>{confirmationMessage}</ModalDescription>
          <StyledButton
            data-testid="modalConfirmButton"
            $fill
            onClick={handleConfirmationSubmit}
            buttonType="primary1"
            disabled={isUpdateOrderStatusLoading}
          >
            {t(`${TP}.continue`, 'Yes, Continue')}
          </StyledButton>
          <StyledButton
            data-testid="modalCancelButton"
            $fill
            onClick={() => setIsConfirming(false)}
            buttonType="secondary"
          >
            {t(`${TP}.no`, 'No')}
          </StyledButton>
        </SideSheetModal>
      </SideSheet.ModalOverlay>
      <SideSheet.Body>
        <BodyContent>
          {!!errors?.length && <ErrorBanner data-testid="errors" message={errors[0]} />}
          <TitleWrapper>
            <Title data-testid="buyerTitle">{buyerTitle}</Title>
            {isIssueResolved && <SVGIcon data-testid="resolvedCheckmark" name="circleCheck" />}
          </TitleWrapper>
          <Description data-testid="buyerDescription">{buyerDescription}</Description>
          {discountPercentageDisplay > 0 && (
            <GridWrapper data-testid="discountGrid">
              <GridRow data-testid="discountAmount">
                <GridText>{t(`${TP}.sellerDiscount`, 'Seller Discount')}</GridText>
                <div>
                  {discountAmountDisplay && <span>{`-${discountAmountDisplay}`}</span>}
                  <span>{`(${discountPercentageDisplay}%)`}</span>
                </div>
              </GridRow>
              {discountPercentageDisplay > 0 && !!paymentTypeDisplay && (
                <GridRow data-testid="discountPaymentMethod">
                  <GridText>{t(`${TP}.paymentMethod`, 'Payment Method')}</GridText>
                  <span>{paymentTypeDisplay}</span>
                </GridRow>
              )}
            </GridWrapper>
          )}
          {!!buyerIssuePictures?.length && (
            <PhotosGrid
              data-testid="photosGrid"
              maxColumns={2}
              title={t(`${TP}.productPhotos`, 'Product Photos')}
              photos={buyerIssuePictures}
              imageSizes={`(min-width: ${sizes.large}px) 300px, 100vw`}
            />
          )}
        </BodyContent>
      </SideSheet.Body>
      <SideSheet.Footer>
        {!!buyerActions?.length && !isConfirming && (
          <ButtonWrapper data-testid="orderIssuesFooterButtons">
            {buyerActions.map((buyerAction, i) => (
              <StyledButton
                buttonType={i === 0 ? 'primary1' : 'secondary'}
                $fill
                onClick={() => handleResolutionSelection(buyerAction)}
                key={buyerAction.buttonAction}
                data-testid="footerButton"
              >
                {buyerAction.buttonTitle}
              </StyledButton>
            ))}
          </ButtonWrapper>
        )}
      </SideSheet.Footer>
    </SideSheet.Responsive>
  )
}

const BodyContent = styled.div`
  padding: 25px 20px 40px;
`
const Title = styled.h3`
  ${fonts.SUBTITLE_2}
  margin: 0;
`
const Description = styled.p`
  ${fonts.BODY_TEXT}
  margin-bottom: 20px;
`
const GridWrapper = styled.div`
  padding: 20px;
  background-color: ${colors.FC2_OFF_WHITE};
  text-transform: uppercase;
  margin-bottom: 30px;
`
const GridRow = styled.div`
  display: flex;
  padding: 0 0 15px;
  justify-content: space-between;
  &:last-child {
    padding: 0;
  }
`
const GridText = styled.p`
  ${fonts.BODY_TEXT}
  margin: 0;
`
const ButtonWrapper = styled.div`
  padding: 20px 20px 40px;
`
const StyledButton = styled(Button)`
  margin-bottom: 15px;
  &:last-child {
    margin-bottom: 0;
  }
`
const ModalHeader = styled.h2`
  ${fonts.SUBTITLE_1}
  margin: 0 0 10px;
`
const ModalDescription = styled.p`
  ${fonts.BODY_TEXT}
  margin-bottom: 30px;
`
const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`
